import React from 'react';
import styled from 'styled-components';

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    
    &::-webkit-scrollbar {
        height: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    background: white;
    font-size: 14px;
`;

const TableHead = styled.thead`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    th {
        padding: 16px 20px;
        text-align: left;
        font-weight: 600;
        font-size: 15px;
        border: none;
        
        &:first-child {
            border-top-left-radius: 12px;
        }
        
        &:last-child {
            border-top-right-radius: 12px;
        }
    }
`;

const TableBody = styled.tbody`
    tr {
        border-bottom: 1px solid #f0f0f0;
        transition: all 0.2s ease;
        
        &:hover {
            background-color: #f5f7fa;
        }
        
        &:last-child {
            border-bottom: none;
            
            td:first-child {
                border-bottom-left-radius: 12px;
            }
            
            td:last-child {
                border-bottom-right-radius: 12px;
            }
        }
        
        &.row-changed {
            background-color: #fff7e6;
            
            &:hover {
                background-color: #ffe7ba;
            }
        }
    }
    
    td {
        padding: 14px 20px;
        border: none;
    }
`;

const FieldColumn = styled.th`
    width: 40%;
`;

const DataColumn = styled.th`
    width: 30%;
    text-align: center;
`;

const FieldCell = styled.td`
    font-weight: 500;
    color: #2c3e50;
    display: flex;
    align-items: center;
`;

const FieldIcon = styled.span`
    margin-right: 10px;
    font-size: 18px;
`;

const FieldName = styled.span`
    color: #2c3e50;
`;

const DataCell = styled.td`
    font-family: 'Courier New', Courier, monospace;
    font-size: 14px;
    text-align: center;
`;

const UpdateEuiTable = ({ oldData, newData, getFacilityCategoryName }) => {
    // 格式化显示值
    const formatDisplayValue = (value, type = 'number') => {
        if (value === '-' || value == null) return '-';
        
        if (type === 'cost') {
            return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        
        return Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };

    // 检查是否有变化（用于高亮行）
    const hasChange = (oldValue, newValue, type) => {
        if (oldValue === '-' || oldValue == null || newValue === '-' || newValue == null) {
            return false;
        }
        
        if (type === 'string') {
            return oldValue != newValue;
        }
        
        return oldValue != newValue;
    };

    // 表格数据配置
    const tableRows = [
        {
            field: 'Company Name',
            oldValue: oldData.company,
            newValue: newData.company,
            type: 'string'
        },
        {
            field: 'Facility Name',
            oldValue: oldData.facility,
            newValue: newData.facility,
            type: 'string'
        },
        {
            field: 'Category',
            oldValue: getFacilityCategoryName ? getFacilityCategoryName(oldData.facility_category) : oldData.facility_category,
            newValue: getFacilityCategoryName ? getFacilityCategoryName(newData.facility_category) : newData.facility_category,
            type: 'string'
        },
        {
            field: 'Year Built',
            oldValue: oldData.year || '-',
            newValue: newData.year || '-',
            type: 'string'
        },
        {
            field: 'Total EUI (kBtu/sq-yr)',
            oldValue: oldData.total_eui,
            newValue: newData.total_eui,
            type: 'number',
            icon: '📊'
        },
        {
            field: 'Electric EUI (kBtu/sq-yr)',
            oldValue: oldData.eui_elec,
            newValue: newData.eui_elec,
            type: 'number',
            icon: '⚡'
        },
        {
            field: 'Natural Gas EUI (kBtu/sq-yr)',
            oldValue: oldData.eui_ng,
            newValue: newData.eui_ng,
            type: 'number',
            icon: '🔥'
        },
        {
            field: 'Total Energy Costs (Annual $)',
            oldValue: oldData.total_costs,
            newValue: newData.total_costs,
            type: 'cost',
            icon: '💰'
        },
        {
            field: 'Energy Costs ($/sqft)',
            oldValue: oldData.costs_per_sqft,
            newValue: newData.costs_per_sqft,
            type: 'cost',
            icon: '💵'
        },
        {
            field: 'Size (sqft)',
            oldValue: oldData.size,
            newValue: newData.size,
            type: 'number',
            icon: '📐'
        },
        {
            field: 'Climate Zone',
            oldValue: oldData.climate_zone || '-',
            newValue: newData.climate_zone || '-',
            type: 'string',
            icon: '🌡️'
        },
        {
            field: 'Region',
            oldValue: oldData.region || '-',
            newValue: newData.region || '-',
            type: 'string',
            icon: '🌍'
        },
        {
            field: 'City',
            oldValue: oldData.city || '-',
            newValue: newData.city || '-',
            type: 'string',
            icon: '🏙️'
        },
        {
            field: 'State',
            oldValue: oldData.state || '-',
            newValue: newData.state || '-',
            type: 'string',
            icon: '📍'
        },
        {
            field: 'Country',
            oldValue: oldData.country || '-',
            newValue: newData.country || '-',
            type: 'string',
            icon: '🌐'
        }
    ];

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <tr>
                        <FieldColumn>Field</FieldColumn>
                        <DataColumn>Previous Data</DataColumn>
                        <DataColumn>Updated Data</DataColumn>
                    </tr>
                </TableHead>
                <TableBody>
                    {tableRows.map((row, index) => {
                        const isChanged = hasChange(row.oldValue, row.newValue, row.type);
                        
                        return (
                            <tr 
                                key={index} 
                                className={isChanged ? 'row-changed' : ''}
                            >
                                <FieldCell>
                                    {row.icon && <FieldIcon>{row.icon}</FieldIcon>}
                                    <FieldName>{row.field}</FieldName>
                                </FieldCell>
                                <DataCell>
                                    {row.type === 'string' 
                                        ? row.oldValue 
                                        : formatDisplayValue(row.oldValue, row.type)}
                                </DataCell>
                                <DataCell>
                                    {row.type === 'string' 
                                        ? row.newValue 
                                        : formatDisplayValue(row.newValue, row.type)}
                                </DataCell>
                            </tr>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default UpdateEuiTable;

